import { useState, useEffect } from "react";
import { apiFetch } from "../lib/api.ts";
import type { Project } from "./useProjects.ts";

export interface ProprietaryTool {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  website: string | null;
  logo_url: string | null;
  category_id: number | null;
}

export interface AlternativeProject extends Project {
  commits_3mo: number;
  last_commit_at: string | null;
  validated_at: string | null;
}

interface AlternativeResponse {
  data: {
    tool: ProprietaryTool;
    alternatives: AlternativeProject[];
  };
}

interface AllToolsResponse {
  data: (ProprietaryTool & { alternative_count: number })[];
}

export function useAlternative(slug: string) {
  const [tool, setTool] = useState<ProprietaryTool | null>(null);
  const [alternatives, setAlternatives] = useState<AlternativeProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);

    apiFetch<AlternativeResponse>(`/alternatives/${slug}`)
      .then((res) => {
        if (cancelled) return;
        setTool(res.data.tool);
        setAlternatives(res.data.alternatives);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  return { tool, alternatives, loading, error };
}

export function useAllTools() {
  const [tools, setTools] = useState<(ProprietaryTool & { alternative_count: number })[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiFetch<AllToolsResponse>("/alternatives")
      .then((res) => {
        if (cancelled) return;
        setTools(res.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { tools, loading };
}
